import { invalidateProjectFinancials } from "@/lib/invalidateProjectFinancials";
import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Trash2, ChevronLeft, ChevronRight, Check, Link2, AlertTriangle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

interface ExtractedLine {
  line_number?: number;
  date?: string;
  product_or_service?: string;
  description: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  line_total: number;
}

interface MatchedEntity {
  id: string;
  name: string;
  score: number;
}

interface ExtractedInvoice {
  fileName: string;
  filePath: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  terms: string;
  poNumber: string;
  projectName: string;
  vendorName: string;
  vendorAddress: string;
  vendorEmail: string;
  vendorPhone: string;
  billToName: string;
  billToCompany: string;
  billToAddress: string;
  lineItems: ExtractedLine[];
  subtotal: number;
  tax: number;
  discount: number;
  shipping: number;
  retainage: number;
  amountPaid: number;
  balanceDue: number;
  notes: string;
  paymentMethods: string;
  saved: boolean;
  matchedProject?: MatchedEntity | null;
  matchedCustomer?: MatchedEntity | null;
  projectLinkConfirmed?: boolean;
  customerLinkConfirmed?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string;
  onSuccess: () => void;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

type Stage = "upload" | "extracting" | "review" | "saving";

/* ── Fuzzy match: normalized token-overlap scoring ── */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenScore(needle: string, haystack: string): number {
  const n = normalize(needle);
  const h = normalize(haystack);
  if (!n || !h) return 0;
  if (n === h) return 1;

  const nCompact = n.replace(/\s+/g, "");
  const hCompact = h.replace(/\s+/g, "");
  if (nCompact === hCompact) return 0.98;
  if (h.includes(n) || n.includes(h)) {
    const ratio = Math.min(n.length, h.length) / Math.max(n.length, h.length);
    return 0.82 + ratio * 0.12;
  }

  const nTokens = n.split(" ").filter((t) => t.length >= 3);
  const hTokens = h.split(" ").filter((t) => t.length >= 3);
  if (nTokens.length === 0 || hTokens.length === 0) return 0;

  let matched = 0;
  for (const nt of nTokens) {
    if (hTokens.some((ht) => ht === nt || ht.includes(nt) || nt.includes(ht))) matched++;
  }

  const overlap = matched / nTokens.length;
  const coveragePenalty = Math.min(nTokens.length / Math.max(hTokens.length, 1), 1);
  return overlap * (0.75 + coveragePenalty * 0.25);
}

function bestMatch(needle: string, candidates: { id: string; name: string }[], threshold = 0.78): MatchedEntity | null {
  if (!needle.trim()) return null;
  let best: MatchedEntity | null = null;
  let secondBest = 0;

  for (const c of candidates) {
    const score = tokenScore(needle, c.name);
    if (!best || score > best.score) {
      secondBest = best?.score || 0;
      best = { id: c.id, name: c.name, score };
    } else if (score > secondBest) {
      secondBest = score;
    }
  }

  if (!best || best.score < threshold) return null;
  if (best.score - secondBest < 0.08 && best.score < 0.95) return null;
  return best;
}

export const InvoiceUploadDialog = ({ open, onOpenChange, projectId, onSuccess }: Props) => {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>("upload");
  const [dragOver, setDragOver] = useState(false);

  const [extractionProgress, setExtractionProgress] = useState({ done: 0, total: 0, currentFile: "" });
  const [invoices, setInvoices] = useState<ExtractedInvoice[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [cutoverDate, setCutoverDate] = useState<string | null>(null);

  // Load company cutover date when dialog opens (required for invoice approval)
  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return;
      const { data } = await supabase
        .from("company_settings")
        .select("accounting_cutover_date")
        .eq("company_id", companyId)
        .maybeSingle();
      setCutoverDate((data as any)?.accounting_cutover_date ?? null);
    })();
  }, [open]);

  const reset = () => {
    setStage("upload");
    setDragOver(false);
    setExtractionProgress({ done: 0, total: 0, currentFile: "" });
    setInvoices([]);
    setCurrentIdx(0);
  };

  const handleClose = (o: boolean) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const extractSingleFile = async (file: File, companyId: string): Promise<ExtractedInvoice | null> => {
    const path = `${companyId}/${Date.now()}_${file.name}`;
    const { error: uploadErr } = await supabase.storage
      .from("invoice-attachments")
      .upload(path, file, { contentType: "application/pdf" });
    if (uploadErr) {
      toast.error(`Upload failed for "${file.name}": ${uploadErr.message}`);
      return null;
    }

    const { data: fnData, error: fnErr } = await supabase.functions.invoke("extract-invoice", {
      body: { file_path: path },
    });

    if (fnErr || fnData?.error) {
      toast.error(`Extraction failed for "${file.name}": ${fnErr?.message || fnData?.error}`);
      return null;
    }

    const d = fnData.data;
    return {
      fileName: file.name,
      filePath: path,
      invoiceNumber: d.invoice_number || "",
      invoiceDate: d.invoice_date || new Date().toISOString().slice(0, 10),
      dueDate: d.due_date || "",
      terms: d.terms || "",
      poNumber: d.po_number || "",
      projectName: d.project_name || "",
      vendorName: d.vendor_name || "",
      vendorAddress: d.vendor_address || "",
      vendorEmail: d.vendor_email || "",
      vendorPhone: d.vendor_phone || "",
      billToName: d.bill_to_name || "",
      billToCompany: d.bill_to_company || "",
      billToAddress: d.bill_to_address || "",
      lineItems: d.line_items || [],
      subtotal: d.subtotal || 0,
      tax: d.tax || 0,
      discount: d.discount || 0,
      shipping: d.shipping || 0,
      retainage: d.retainage || 0,
      amountPaid: d.amount_paid || 0,
      balanceDue: d.balance_due || 0,
      notes: d.notes || "",
      paymentMethods: d.payment_methods || "",
      saved: false,
      matchedProject: null,
      matchedCustomer: null,
    };
  };

  /* ── Smart matching: run after extraction ── */
  const runSmartMatching = async (extracted: ExtractedInvoice[], companyId: string): Promise<ExtractedInvoice[]> => {
    // Fetch projects and customers for matching
    const sb = supabase as any;
    const projRes = await sb.from("projects").select("id, name").eq("is_deleted", false).limit(500);
    const custRes = await sb.from("customers").select("id, name").eq("is_deleted", false).limit(500);

    const projects: { id: string; name: string }[] = (projRes.data || []).map((p: any) => ({ id: p.id, name: p.name }));
    const customers: { id: string; name: string }[] = (custRes.data || []).map((c: any) => ({ id: c.id, name: c.name }));

    return extracted.map(inv => {
      // Match project: use extracted projectName
      let matchedProject: MatchedEntity | null = null;
      if (inv.projectName && !projectId) {
        matchedProject = bestMatch(inv.projectName, projects, 0.4);
      }

      // Match customer: use billToCompany first, then billToName
      let matchedCustomer: MatchedEntity | null = null;
      const customerSearch = inv.billToCompany || inv.billToName;
      if (customerSearch) {
        matchedCustomer = bestMatch(customerSearch, customers, 0.4);
      }

      return { ...inv, matchedProject, matchedCustomer };
    });
  };

  const processFiles = async (fileList: File[]) => {
    const allowed = fileList.filter(f => {
      if (f.type !== "application/pdf" && !/\.pdf$/i.test(f.name)) {
        toast.error(`"${f.name}" — only PDF files are accepted`);
        return false;
      }
      if (f.size > 50 * 1024 * 1024) {
        toast.error(`"${f.name}" — file too large (max 50 MB)`);
        return false;
      }
      return true;
    });
    if (allowed.length === 0) return;

    setStage("extracting");
    setExtractionProgress({ done: 0, total: allowed.length, currentFile: allowed[0].name });

    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Could not determine company");

      const results: ExtractedInvoice[] = [];
      for (let i = 0; i < allowed.length; i++) {
        setExtractionProgress({ done: i, total: allowed.length, currentFile: allowed[i].name });
        const inv = await extractSingleFile(allowed[i], companyId);
        if (inv) results.push(inv);
      }

      if (results.length === 0) {
        toast.error("No invoices could be extracted");
        setStage("upload");
        return;
      }

      // Run smart matching against existing projects & customers
      const matched = await runSmartMatching(results, companyId);

      const matchCount = matched.filter(m => m.matchedProject || m.matchedCustomer).length;
      setInvoices(matched);
      setCurrentIdx(0);
      setStage("review");

      if (results.length > 1) {
        toast.success(`${results.length} invoices extracted — review each one below`);
      }
      if (matchCount > 0) {
        toast.success(`Smart match: auto-linked ${matchCount} invoice${matchCount > 1 ? "s" : ""} to existing records`);
      }
    } catch (err: any) {
      toast.error(err.message || "Extraction failed");
      setStage("upload");
    }
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) processFiles(files);
  }, []);

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) processFiles(files);
  };

  const inv = invoices[currentIdx];

  const updateInv = (updates: Partial<ExtractedInvoice>) => {
    setInvoices(prev => prev.map((b, i) => i === currentIdx ? { ...b, ...updates } : b));
  };

  const updateLine = (idx: number, field: keyof ExtractedLine, value: string) => {
    if (!inv) return;
    const newLines = inv.lineItems.map((l, i) => {
      if (i !== idx) return l;
      if (field === "description" || field === "unit" || field === "product_or_service") return { ...l, [field]: value };
      const num = parseFloat(value) || 0;
      const updated = { ...l, [field]: num };
      if (field === "quantity" || field === "unit_price") {
        updated.line_total = updated.quantity * updated.unit_price;
      }
      return updated;
    });
    updateInv({ lineItems: newLines });
  };

  const removeLine = (idx: number) => {
    if (!inv) return;
    updateInv({ lineItems: inv.lineItems.filter((_, i) => i !== idx) });
  };

  const computedTotal = inv ? inv.lineItems.reduce((s, l) => s + (l.line_total || 0), 0) : 0;
  const finalTotal = inv ? computedTotal + inv.tax - inv.discount + inv.shipping : 0;

  // Validation: gather all blocking issues so user must fix before save
  const validationIssues: string[] = (() => {
    if (!inv) return [];
    const issues: string[] = [];
    if (!cutoverDate) issues.push("Accounting cutover date is not set in Company Settings");
    if (!inv.invoiceNumber?.trim()) issues.push("Invoice number is required");
    if (!inv.invoiceDate) issues.push("Invoice date is required");
    if (cutoverDate && inv.invoiceDate && inv.invoiceDate < cutoverDate) {
      issues.push(`Invoice date must be on or after cutover (${cutoverDate})`);
    }
    const validLines = inv.lineItems.filter(l => l.description?.trim());
    if (validLines.length === 0) issues.push("Add at least one line item with a description");
    if (computedTotal <= 0) issues.push("Invoice total must be greater than zero");
    const resolvedProjectId = projectId || (inv.projectLinkConfirmed && inv.matchedProject?.id);
    if (!resolvedProjectId) issues.push("Link the invoice to a project");
    const resolvedCustomerId = inv.customerLinkConfirmed && inv.matchedCustomer?.id;
    if (!resolvedCustomerId) issues.push("Link the invoice to a customer");
    return issues;
  })();
  const canSave = validationIssues.length === 0;

  const saveCurrent = async () => {
    if (!canSave) {
      toast.error(validationIssues[0]);
      return;
    }
    if (!inv) return;
    setStage("saving");

    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Could not determine company");

      // Check for duplicate invoice number
      if (inv.invoiceNumber) {
        const { count } = await supabase
          .from("invoices")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .eq("invoice_number", inv.invoiceNumber)
          .is("deleted_at", null);

        if (count && count > 0) {
          toast.error(`Invoice #${inv.invoiceNumber} already exists. Change the number or edit the existing invoice.`);
          setStage("review");
          return;
        }
      }

      // Resolve project_id: explicit prop > confirmed smart match
      const resolvedProjectId = projectId || (inv.projectLinkConfirmed && inv.matchedProject?.id) || null;
      // Resolve customer_id: confirmed smart match only
      const resolvedCustomerId = (inv.customerLinkConfirmed && inv.matchedCustomer?.id) || null;

      // Step 1: Insert invoice as DRAFT so RLS allows line item inserts
      const invoicePayload = {
        company_id: companyId,
        project_id: resolvedProjectId,
        customer_id: resolvedCustomerId,
        invoice_number: inv.invoiceNumber || null,
        invoice_date: inv.invoiceDate || new Date().toISOString().slice(0, 10),
        due_date: inv.dueDate || null,
        subtotal: computedTotal,
        total: finalTotal,
        status: "draft" as const,
        pdf_path: inv.filePath,
        notes: inv.notes || null,
        bill_to_name: inv.billToName || null,
        bill_to_company: inv.billToCompany || null,
        bill_to_address: inv.billToAddress || null,
        po_number: inv.poNumber || null,
        terms_raw: inv.terms || null,
        tax_amount: inv.tax || 0,
        amount_paid: 0,
        source: "pdf_upload",
        needs_project_assignment: !resolvedProjectId,
        needs_customer_assignment: !resolvedCustomerId,
      } as any;
      const { data: savedInv, error: invErr } = await supabase
        .from("invoices")
        .insert(invoicePayload)
        .select("id")
        .single();

      if (invErr) throw new Error("Invoice insert failed: " + invErr.message);

      // Step 2: Insert line items (allowed because parent invoice is draft)
      const lines = inv.lineItems
        .filter((l) => l.description && String(l.description).trim().length > 0)
        .map((l) => ({
          invoice_id: savedInv!.id,
          company_id: companyId,
          description: String(l.description).trim(),
          quantity: Number(l.quantity) || 1,
          unit_price: Number(l.unit_price) || 0,
          line_total: Number(l.line_total) || 0,
        }));

      if (lines.length > 0) {
        const { error: lineErr } = await supabase
          .from("invoice_lines")
          .insert(lines as any);
        if (lineErr) {
          console.error("[InvoiceUpload] Line items failed", {
            lineErr,
            savedInvoiceId: savedInv?.id,
            resolvedProjectId,
            resolvedCustomerId,
            lineCount: lines.length,
            firstLine: lines[0],
          });
          await supabase.rpc("hard_delete_invoice", { p_invoice_id: savedInv!.id });
          throw new Error("Line items could not be saved — invoice rolled back. " + (lineErr?.message || "RLS block"));
        }
      } else {
        // No valid lines — rollback
        await supabase.rpc("hard_delete_invoice", { p_invoice_id: savedInv!.id });
        throw new Error("No valid line items found — invoice not saved");
      }

      // Step 3: Approve the invoice now that lines are saved
      const { error: approveErr } = await supabase.rpc("approve_invoice", {
        p_invoice_id: savedInv!.id,
      });
      if (approveErr) {
        console.error("Auto-approve failed:", approveErr.message);
      }

      setInvoices(prev => prev.map((b, i) => i === currentIdx ? { ...b, saved: true } : b));

      const remaining = invoices.filter((b, i) => !b.saved && i !== currentIdx);
      if (remaining.length > 0) {
        toast.success(`Invoice saved — ${remaining.length} remaining`);
        const nextIdx = invoices.findIndex((b, i) => i > currentIdx && !b.saved);
        setCurrentIdx(nextIdx >= 0 ? nextIdx : invoices.findIndex((b, i) => i !== currentIdx && !b.saved));
        setStage("review");
      } else {
        toast.success(invoices.length > 1 ? `All ${invoices.length} invoices imported successfully!` : "Invoice imported successfully");
        invalidateProjectFinancials(qc);
        handleClose(false);
        onSuccess();
      }
    } catch (err: any) {
      toast.error(err.message || "Save failed");
      setStage("review");
    }
  };

  const savedCount = invoices.filter(b => b.saved).length;
  const unsavedCount = invoices.length - savedCount;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Upload Invoice{invoices.length > 1 ? "s" : ""} PDF
            {stage === "review" && invoices.length > 1 && (
              <Badge variant="secondary" className="ml-2 text-xs font-normal">
                {currentIdx + 1} of {invoices.length} · {savedCount} saved
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {stage === "upload" && "Drag and drop PDF invoices or click to browse. You can upload multiple invoices at once."}
            {stage === "extracting" && `Extracting invoice data… (${extractionProgress.done + 1} of ${extractionProgress.total})`}
            {stage === "review" && (invoices.length > 1
              ? "Review each extracted invoice and save them individually."
              : "Review the extracted data below and make corrections before saving.")}
            {stage === "saving" && "Saving invoice…"}
          </DialogDescription>
        </DialogHeader>

        {/* Upload Stage */}
        {stage === "upload" && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium">Drop PDFs here or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">PDF files only · Max 50 MB each · Multiple files supported</p>
            <input ref={fileRef} type="file" accept=".pdf,application/pdf" multiple className="hidden" onChange={onFileSelect} />
          </div>
        )}

        {/* Extracting Stage */}
        {stage === "extracting" && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="w-full max-w-xs">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 rounded-full"
                  style={{ width: `${((extractionProgress.done + 0.5) / extractionProgress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Extracting {extractionProgress.done + 1} of {extractionProgress.total}…
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span className="truncate max-w-[200px]">{extractionProgress.currentFile}</span>
            </div>
          </div>
        )}

        {/* Review Stage */}
        {stage === "review" && inv && (
          <div className="space-y-5">
            {/* Multi-invoice navigation */}
            {invoices.length > 1 && (
              <div className="flex items-center justify-between bg-muted/40 rounded-md px-3 py-2">
                <Button variant="ghost" size="sm" disabled={currentIdx === 0} onClick={() => setCurrentIdx(i => i - 1)}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <div className="flex items-center gap-1.5">
                  {invoices.map((b, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentIdx(i)}
                      className={`w-7 h-7 rounded-full text-xs font-medium transition-colors ${
                        i === currentIdx
                          ? "bg-primary text-primary-foreground"
                          : b.saved
                          ? "bg-primary/20 text-primary"
                          : "bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/30"
                      }`}
                      title={b.fileName}
                    >
                      {b.saved ? <Check className="h-3 w-3 mx-auto" /> : i + 1}
                    </button>
                  ))}
                </div>
                <Button variant="ghost" size="sm" disabled={currentIdx === invoices.length - 1} onClick={() => setCurrentIdx(i => i + 1)}>
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}

            {/* File name indicator */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span className="truncate">{inv.fileName}</span>
              {inv.saved && <Badge variant="secondary" className="text-xs">Saved</Badge>}
            </div>

            {/* ── Smart Match Results ── */}
            {(inv.matchedProject || inv.matchedCustomer || inv.projectName || inv.billToCompany) && (
              <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" /> Smart Match
                </p>

                {/* Project match */}
                {inv.projectName && (
                  <div className="flex items-center justify-between text-sm gap-2">
                    <span className="text-muted-foreground truncate">
                      Project: <span className="font-medium text-foreground">"{inv.projectName}"</span>
                    </span>
                    {inv.matchedProject ? (
                      inv.projectLinkConfirmed ? (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="default" className="text-xs gap-1">
                            <Check className="h-3 w-3" /> {inv.matchedProject.name}
                          </Badge>
                          {!inv.saved && (
                            <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground px-1.5"
                              onClick={() => updateInv({ projectLinkConfirmed: false })}>
                              Undo
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground">Link to <strong>{inv.matchedProject.name}</strong>?</span>
                          <Button size="sm" variant="default" className="h-6 text-xs px-2"
                            onClick={() => updateInv({ projectLinkConfirmed: true })} disabled={inv.saved}>
                            <Check className="h-3 w-3 mr-1" /> Yes
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-muted-foreground"
                            onClick={() => updateInv({ matchedProject: null })} disabled={inv.saved}>
                            No
                          </Button>
                        </div>
                      )
                    ) : projectId ? (
                      <Badge variant="secondary" className="text-xs">Using current project</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-muted-foreground gap-1">
                        <AlertTriangle className="h-3 w-3" /> No match found
                      </Badge>
                    )}
                  </div>
                )}

                {/* Customer match */}
                {(inv.billToCompany || inv.billToName) && (
                  <div className="flex items-center justify-between text-sm gap-2">
                    <span className="text-muted-foreground truncate">
                      Customer: <span className="font-medium text-foreground">"{inv.billToCompany || inv.billToName}"</span>
                    </span>
                    {inv.matchedCustomer ? (
                      inv.customerLinkConfirmed ? (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="default" className="text-xs gap-1">
                            <Check className="h-3 w-3" /> {inv.matchedCustomer.name}
                          </Badge>
                          {!inv.saved && (
                            <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground px-1.5"
                              onClick={() => updateInv({ customerLinkConfirmed: false })}>
                              Undo
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground">Link to <strong>{inv.matchedCustomer.name}</strong>?</span>
                          <Button size="sm" variant="default" className="h-6 text-xs px-2"
                            onClick={() => updateInv({ customerLinkConfirmed: true })} disabled={inv.saved}>
                            <Check className="h-3 w-3 mr-1" /> Yes
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-muted-foreground"
                            onClick={() => updateInv({ matchedCustomer: null })} disabled={inv.saved}>
                            No
                          </Button>
                        </div>
                      )
                    ) : (
                      <Badge variant="outline" className="text-xs text-muted-foreground gap-1">
                        <AlertTriangle className="h-3 w-3" /> No match found
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Header Fields */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Invoice Number</Label>
                <Input value={inv.invoiceNumber} onChange={e => updateInv({ invoiceNumber: e.target.value })} placeholder="Auto-generate if blank" disabled={inv.saved} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Invoice Date</Label>
                <Input type="date" value={inv.invoiceDate} onChange={e => updateInv({ invoiceDate: e.target.value })} disabled={inv.saved} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Due Date</Label>
                <Input type="date" value={inv.dueDate} onChange={e => updateInv({ dueDate: e.target.value })} disabled={inv.saved} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Terms</Label>
                <Input value={inv.terms} onChange={e => updateInv({ terms: e.target.value })} placeholder="e.g. Net 30" disabled={inv.saved} />
              </div>
            </div>

            {(inv.poNumber || inv.projectName) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">PO Number</Label>
                  <Input value={inv.poNumber} onChange={e => updateInv({ poNumber: e.target.value })} disabled={inv.saved} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Project Name</Label>
                  <Input value={inv.projectName} onChange={e => updateInv({ projectName: e.target.value })} disabled={inv.saved} />
                </div>
              </div>
            )}

            <Separator />

            {/* Vendor & Bill-To */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vendor / Payee</p>
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input value={inv.vendorName} onChange={e => updateInv({ vendorName: e.target.value })} disabled={inv.saved} />
                </div>
                {inv.vendorAddress && (
                  <div className="space-y-1">
                    <Label className="text-xs">Address</Label>
                    <Textarea className="min-h-[60px] text-xs" value={inv.vendorAddress} onChange={e => updateInv({ vendorAddress: e.target.value })} disabled={inv.saved} />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {inv.vendorEmail && (
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input className="text-xs" value={inv.vendorEmail} onChange={e => updateInv({ vendorEmail: e.target.value })} disabled={inv.saved} />
                    </div>
                  )}
                  {inv.vendorPhone && (
                    <div className="space-y-1">
                      <Label className="text-xs">Phone</Label>
                      <Input className="text-xs" value={inv.vendorPhone} onChange={e => updateInv({ vendorPhone: e.target.value })} disabled={inv.saved} />
                    </div>
                  )}
                </div>
              </div>

              {(inv.billToName || inv.billToCompany || inv.billToAddress) && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bill To</p>
                  {inv.billToName && (
                    <div className="space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input className="text-xs" value={inv.billToName} onChange={e => updateInv({ billToName: e.target.value })} disabled={inv.saved} />
                    </div>
                  )}
                  {inv.billToCompany && (
                    <div className="space-y-1">
                      <Label className="text-xs">Company</Label>
                      <Input className="text-xs" value={inv.billToCompany} onChange={e => updateInv({ billToCompany: e.target.value })} disabled={inv.saved} />
                    </div>
                  )}
                  {inv.billToAddress && (
                    <div className="space-y-1">
                      <Label className="text-xs">Address</Label>
                      <Textarea className="min-h-[60px] text-xs" value={inv.billToAddress} onChange={e => updateInv({ billToAddress: e.target.value })} disabled={inv.saved} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* Line Items */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Line Items</p>
              <div className="border rounded-md overflow-auto max-h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Description</TableHead>
                      <TableHead className="w-20">Qty</TableHead>
                      <TableHead className="w-28">Unit Price</TableHead>
                      <TableHead className="w-28">Total</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inv.lineItems.map((line, i) => (
                      <TableRow key={i}>
                        <TableCell className="p-1">
                          <Input className="h-8 text-xs" value={line.description} onChange={e => updateLine(i, "description", e.target.value)} disabled={inv.saved} />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input className="h-8 text-xs text-right" type="number" value={line.quantity} onChange={e => updateLine(i, "quantity", e.target.value)} disabled={inv.saved} />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input className="h-8 text-xs text-right" type="number" step="0.01" value={line.unit_price} onChange={e => updateLine(i, "unit_price", e.target.value)} disabled={inv.saved} />
                        </TableCell>
                        <TableCell className="p-1 text-right text-sm font-medium">{fmt(line.line_total)}</TableCell>
                        <TableCell className="p-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => removeLine(i)} disabled={inv.saved}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">{inv.lineItems.length} line item{inv.lineItems.length !== 1 ? "s" : ""}</p>
            </div>

            <Separator />

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{fmt(computedTotal)}</span>
                </div>
                {inv.tax > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Tax</span>
                    <Input className="h-7 w-24 text-xs text-right" type="number" step="0.01" value={inv.tax} onChange={e => updateInv({ tax: parseFloat(e.target.value) || 0 })} disabled={inv.saved} />
                  </div>
                )}
                {inv.discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="text-destructive">-{fmt(inv.discount)}</span>
                  </div>
                )}
                {inv.shipping > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span>{fmt(inv.shipping)}</span>
                  </div>
                )}
                {inv.retainage > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Retainage</span>
                    <span>-{fmt(inv.retainage)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>{fmt(finalTotal)}</span>
                </div>
                {inv.amountPaid > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Amount Paid</span>
                    <span>-{fmt(inv.amountPaid)}</span>
                  </div>
                )}
                {(inv.balanceDue > 0 || inv.amountPaid > 0) && (
                  <div className="flex justify-between font-semibold text-primary">
                    <span>Balance Due</span>
                    <span>{fmt(inv.balanceDue || (finalTotal - inv.amountPaid))}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes & Payment */}
            {(inv.notes || inv.paymentMethods) && (
              <>
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {inv.notes && (
                    <div className="space-y-1">
                      <Label className="text-xs">Notes / Memo</Label>
                      <Textarea className="min-h-[60px] text-xs" value={inv.notes} onChange={e => updateInv({ notes: e.target.value })} disabled={inv.saved} />
                    </div>
                  )}
                  {inv.paymentMethods && (
                    <div className="space-y-1">
                      <Label className="text-xs">Payment Methods</Label>
                      <Textarea className="min-h-[60px] text-xs" value={inv.paymentMethods} onChange={e => updateInv({ paymentMethods: e.target.value })} disabled={inv.saved} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Saving Stage */}
        {stage === "saving" && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
            <span className="text-sm text-muted-foreground">Saving invoice…</span>
          </div>
        )}

        {stage === "review" && inv && (
          <>
            {!inv.saved && validationIssues.length > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive space-y-1">
                <p className="font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Complete the following before saving:
                </p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {validationIssues.map((msg, i) => <li key={i}>{msg}</li>)}
                </ul>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              {inv.saved ? (
                <Button disabled variant="secondary">
                  <Check className="h-4 w-4 mr-1" /> Saved
                </Button>
              ) : (
                <Button onClick={saveCurrent} disabled={!canSave}>
                  {unsavedCount > 1 ? `Save & Continue (${unsavedCount} remaining)` : "Save Invoice"}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
